import React, { useState } from "react";
import { format, parseISO, subDays, addDays } from "date-fns";
import { Link } from "react-router-dom";
import {
  useGetTimesheetsQuery,
  useUpdateTimesheetMutation,
  useVoidTimesheetMutation,
  useAddTimesheetMutation,
  useApproveAllTimesheetsMutation,
} from "@/store/timesheets/apis";
import { Loading } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { TrashIcon, CheckIcon, XIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  faArrowLeft,
  faArrowRight,
  faCalendar,
  faCheckCircle,
  faEdit,
} from "@awesome.me/kit-9096fb77ac/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn, formatDateWithOrdinal } from "@/lib/utils";
import { CrewNavigation } from "@/components/Crew/tabs";

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const formatTime = (dateString: string) => {
  return format(parseISO(dateString), "h:mm a");
};

interface TimeEditorProps {
  hour: number;
  minute: number;
  meridiem: "AM" | "PM";
  onSave: (hour: number, minute: number, meridiem: "AM" | "PM") => void;
  onCancel: () => void;
}

const TimeEditor: React.FC<TimeEditorProps> = ({
  hour,
  minute,
  meridiem,
  onSave,
  onCancel,
}) => {
  const [editHour, setEditHour] = useState(hour);
  const [editMinute, setEditMinute] = useState(minute);
  const [editMeridiem, setEditMeridiem] = useState<"AM" | "PM">(meridiem);

  return (
    <div className="flex items-center gap-1">
      <Input
        // type="number"
        min={1}
        max={12}
        value={editHour}
        onChange={(e) => setEditHour(parseInt(e.target.value) || 0)}
        className="w-10"
        onFocus={(e) => e.target.select()}
      />
      <Input
        // type="number"
        min={0}
        max={59}
        value={editMinute}
        onChange={(e) => setEditMinute(parseInt(e.target.value) || 0)}
        className="w-10"
        onFocus={(e) => e.target.select()}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => setEditMeridiem(editMeridiem === "AM" ? "PM" : "AM")}
      >
        {editMeridiem}
      </Button>
      <Button
        size="sm"
        className="bg-green-600 hover:bg-green-700 text-white"
        onClick={() => onSave(editHour, editMinute, editMeridiem)}
      >
        <CheckIcon className="h-4 w-4" />
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        <XIcon className="h-4 w-4" />
      </Button>
    </div>
  );
};

interface AddTimesheetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  selectedUser: { id: number; name: string } | null;
}

const AddTimesheetDialog: React.FC<AddTimesheetDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
  selectedUser,
}) => {
  const [startHour, setStartHour] = useState<number>(9);
  const [startMinute, setStartMinute] = useState<number>(0);
  const [startMeridiem, setStartMeridiem] = useState<"AM" | "PM">("AM");
  const [endHour, setEndHour] = useState<number>(10);
  const [endMinute, setEndMinute] = useState<number>(0);
  const [endMeridiem, setEndMeridiem] = useState<"AM" | "PM">("AM");
  const [notes, setNotes] = useState("");
  const [addTimesheet] = useAddTimesheetMutation();
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!selectedUser) return;

    try {
      const today = new Date();

      // Set start time
      let startHours24 = startHour;
      if (startMeridiem === "PM" && startHour !== 12) startHours24 += 12;
      if (startMeridiem === "AM" && startHour === 12) startHours24 = 0;

      const startTime = new Date(today);
      startTime.setHours(startHours24, startMinute, 0, 0);

      // Set end time
      let endHours24 = endHour;
      if (endMeridiem === "PM" && endHour !== 12) endHours24 += 12;
      if (endMeridiem === "AM" && endHour === 12) endHours24 = 0;

      const endTime = new Date(today);
      endTime.setHours(endHours24, endMinute, 0, 0);

      await addTimesheet({
        data: {
          on_date: format(today, "yyyy-MM-dd"),
          user_id: selectedUser.id,
          started_at: startTime.toISOString(),
          ended_at: endTime.toISOString(),
          notes: notes || "Manual Entry",
        },
      }).unwrap();

      toast({
        title: "Success",
        description: "Timesheet entry added successfully",
      });

      onSuccess();
      onOpenChange(false);
      setNotes("");
    } catch {
      toast({
        title: "Error",
        description: "Failed to add timesheet entry",
        variant: "destructive",
      });
    }
  };

  if (!selectedUser) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Manual Timesheet Entry</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Start Time</Label>
            <div className="flex items-center gap-1">
              <Input
                className="w-10"
                value={startHour}
                onChange={(e) => setStartHour(parseInt(e.target.value) || 0)}
                onFocus={(e) => e.target.select()}
              />
              <Input
                className="w-10"
                value={startMinute}
                onChange={(e) => setStartMinute(parseInt(e.target.value) || 0)}
                onFocus={(e) => e.target.select()}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setStartMeridiem(startMeridiem === "AM" ? "PM" : "AM")
                }
              >
                {startMeridiem}
              </Button>
            </div>
          </div>

          <div>
            <Label>End Time</Label>
            <div className="flex items-center gap-1">
              <Input
                className="w-10"
                value={endHour}
                onChange={(e) => setEndHour(parseInt(e.target.value) || 0)}
                onFocus={(e) => e.target.select()}
              />
              <Input
                className="w-10"
                value={endMinute}
                onChange={(e) => setEndMinute(parseInt(e.target.value) || 0)}
                onFocus={(e) => e.target.select()}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setEndMeridiem(endMeridiem === "AM" ? "PM" : "AM")
                }
              >
                {endMeridiem}
              </Button>
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter notes"
            />
          </div>

          <Button onClick={handleSubmit} className="w-full">
            Add Entry
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Timesheets: React.FC = () => {
  const { toast } = useToast();
  const [showVoidConfirm, setShowVoidConfirm] = React.useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = React.useState<
    number | null
  >(null);
  const [updateTimesheet] = useUpdateTimesheetMutation();
  const [voidTimesheet] = useVoidTimesheetMutation();
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [approveAllTimesheets] = useApproveAllTimesheetsMutation();
  const {
    data: timesheetData,
    isLoading,
    error,
    refetch,
  } = useGetTimesheetsQuery({ date: date });
  const [editingStartTime, setEditingStartTime] = useState<number | null>(null);
  const [editingEndTime, setEditingEndTime] = useState<number | null>(null);
  const [addDialog, setAddDialog] = useState<{
    isOpen: boolean;
    selectedUser: { id: number; name: string } | null;
  }>({
    isOpen: false,
    selectedUser: null,
  });

  const handleVoidTimesheet = async () => {
    if (!selectedTimesheet) return;

    try {
      await voidTimesheet({
        id: selectedTimesheet,
      }).unwrap();

      toast({
        title: "Success",
        description: "Timesheet voided successfully",
      });

      refetch();
    } catch {
      toast({
        title: "Error",
        description: "Failed to void timesheet",
        variant: "destructive",
      });
    } finally {
      setShowVoidConfirm(false);
      setSelectedTimesheet(null);
    }
  };

  const handleTimeUpdate = async (
    timesheetId: number,
    type: "start" | "end",
    hour: number,
    minute: number,
    meridiem: "AM" | "PM"
  ) => {
    try {
      const timesheet = timesheetData
        ?.flatMap((u) => u.timesheets)
        .find((t) => t.id === timesheetId);

      if (!timesheet) return;

      const originalDate = parseISO(timesheet.on_date);

      // Convert to 24-hour format
      let hours24 = hour;
      if (meridiem === "PM" && hour !== 12) hours24 += 12;
      if (meridiem === "AM" && hour === 12) hours24 = 0;

      const newDate = new Date(originalDate);
      newDate.setHours(hours24);
      newDate.setMinutes(minute);

      await updateTimesheet({
        id: timesheetId,
        data: {
          [type === "start" ? "started_at" : "ended_at"]: newDate.toISOString(),
        },
      }).unwrap();

      toast({
        title: "Success",
        description: "Time updated successfully",
      });

      refetch();
    } catch {
      toast({
        title: "Error",
        description: "Failed to update time",
        variant: "destructive",
      });
    } finally {
      setEditingStartTime(null);
      setEditingEndTime(null);
    }
  };

  const handleApproveAll = async () => {
    if (!timesheetData) return;

    const ids = timesheetData
      .flatMap((user) => user.timesheets)
      .map((timesheet) => timesheet.id);

    await approveAllTimesheets({ ids }).unwrap();

    toast({
      title: "Success",
      description: "All timesheets approved successfully",
    });

    refetch();
  };

  if (isLoading)
    return (
      <div className="p-6 w-full max-w-screen-lg">
        <Loading />
      </div>
    );
  if (error)
    return (
      <div className="p-6 w-full max-w-screen-lg">
        <div>Error loading timesheets</div>
      </div>
    );

  if (!timesheetData || timesheetData.length === 0) {
    return (
      <div className="px-6 w-full max-w-screen-lg">
        <div className="py-2">
          <CrewNavigation />
        </div>
        <Heading
          date={date}
          setDate={setDate}
          approveAllTimesheets={() => {}}
          timesheetData={timesheetData}
        />
        <div className="flex justify-center items-center h-screen w-screen">
          No timesheets found
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 w-full max-w-screen-lg">
      <div className="py-2">
        <CrewNavigation />
      </div>

      <Heading
        date={date}
        setDate={setDate}
        approveAllTimesheets={handleApproveAll}
        timesheetData={timesheetData}
      />

      {timesheetData.map((userData) => (
        <React.Fragment key={userData.user.id}>
          <div className="bg-stone-100 flex flex-row justify-between items-center px-2 py-1">
            <div className="font-semibold py-1 px-2">{userData.user.name}</div>
            <div className="flex flex-row items-center gap-4">
              <div className="flex flex-row items-center gap-2">
                <div className="flex flex-row items-center gap-2">
                  <span className="text-sm font-semibold">
                    {formatDuration(
                      userData.timesheets.reduce(
                        (acc, timesheet) => acc + timesheet.duration_minutes,
                        0
                      )
                    )}
                  </span>
                </div>
              </div>
              <div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setAddDialog({
                      isOpen: true,
                      selectedUser: userData.user,
                    });
                  }}
                >
                  Add Manual Entry
                </Button>
              </div>
            </div>
          </div>

          {userData.timesheets.length === 0 && (
            <div className="flex flex-row items-center gap-2 px-2 py-2">
              <span className="text-sm">No timesheets available</span>
            </div>
          )}

          {userData.timesheets.map((timesheet) => (
            <div
              key={timesheet.id}
              className="grid grid-cols-12 text-sm py-2 border-b border-stone-200 h-12 px-2"
            >
              <div className="col-span-3 flex items-center gap-2">
                {timesheet.approved && (
                  <div className="text-green-600 font-semibold">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </div>
                )}
                {timesheet.move ? (
                  <div className="flex flex-row items-center gap-1">
                    Job:{" "}
                    <Link
                      to={`/move/${timesheet.move.id}`}
                      className="font-semibold underline underline-offset-4"
                      target="_blank"
                    >
                      {timesheet.move.customer_name}
                    </Link>
                  </div>
                ) : (
                  <span>{timesheet.notes || "-"}</span>
                )}
              </div>
              <div className="col-span-5 flex items-center justify-end gap-4 h-full">
                {editingStartTime === timesheet.id ? (
                  <TimeEditor
                    hour={parseTime(timesheet.started_at).hour}
                    minute={parseTime(timesheet.started_at).minute}
                    meridiem={parseTime(timesheet.started_at).meridiem}
                    onSave={(h, m, mer) =>
                      handleTimeUpdate(timesheet.id, "start", h, m, mer)
                    }
                    onCancel={() => setEditingStartTime(null)}
                  />
                ) : (
                  <button
                    className="flex flex-row items-end gap-1 text-right"
                    onClick={() => setEditingStartTime(timesheet.id)}
                  >
                    <span className="leading-none">
                      {formatTime(timesheet.started_at)}
                    </span>
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
                {/* {timesheet.start_place && (
                      <span className="text-sm text-gray-600">
                        <Link
                          to={`https://www.google.com/maps/@${timesheet.start_latitude},${timesheet.start_longitude},15z`}
                          className="text-blue-600 hover:underline"
                          target="_blank"
                        >
                          <FontAwesomeIcon icon={faLocationArrow} />
                          {timesheet.start_place}
                        </Link>
                      </span>
                    )} */}
                <div className="flex flex-row items-end gap-0.5">
                  <FontAwesomeIcon icon={faArrowRight} />
                  {/* {timesheet.start_place} */}
                </div>
                {editingEndTime === timesheet.id ? (
                  <TimeEditor
                    hour={
                      parseTime(timesheet.ended_at || new Date().toISOString())
                        .hour
                    }
                    minute={
                      parseTime(timesheet.ended_at || new Date().toISOString())
                        .minute
                    }
                    meridiem={
                      parseTime(timesheet.ended_at || new Date().toISOString())
                        .meridiem
                    }
                    onSave={(h, m, mer) =>
                      handleTimeUpdate(timesheet.id, "end", h, m, mer)
                    }
                    onCancel={() => setEditingEndTime(null)}
                  />
                ) : (
                  <button
                    className="flex flex-row items-end gap-1 text-right"
                    onClick={() => setEditingEndTime(timesheet.id)}
                  >
                    <span className="leading-none">
                      {timesheet.ended_at
                        ? formatTime(timesheet.ended_at)
                        : "not set"}
                    </span>
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
                {/* {timesheet.end_place && (
                      <span className="text-sm text-gray-600">
                        (
                        <Link
                          to={`https://www.google.com/maps/@${timesheet.end_latitude},${timesheet.end_longitude},15z`}
                          className="text-blue-600 hover:underline"
                          target="_blank"
                        >
                          location
                          {timesheet.end_place}
                        </Link>
                        )
                      </span>
                    )} */}
              </div>
              <div className="col-span-2 flex items-center justify-end">
                {formatDuration(timesheet.duration_minutes)}
              </div>
              <div className="col-span-2 flex items-center justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 gap-1"
                  onClick={() => {
                    setSelectedTimesheet(timesheet.id);
                    setShowVoidConfirm(true);
                  }}
                >
                  <TrashIcon className="h-3 w-3" /> Void
                </Button>
              </div>
            </div>
          ))}
        </React.Fragment>
      ))}

      <AddTimesheetDialog
        open={addDialog.isOpen}
        onOpenChange={(open) => {
          setAddDialog({
            isOpen: open,
            selectedUser: open ? addDialog.selectedUser : null,
          });
        }}
        onSuccess={refetch}
        selectedUser={addDialog.selectedUser}
      />

      <AlertDialog open={showVoidConfirm} onOpenChange={setShowVoidConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void Timesheet</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to void this timesheet? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVoidTimesheet}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Void Timesheet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Heading = ({
  date,
  setDate,
  approveAllTimesheets,
  timesheetData,
}: {
  date: string;
  setDate: (date: string) => void;
  approveAllTimesheets: () => void;
  timesheetData: any;
}) => {
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const today = format(new Date(), "yyyy-MM-dd");
  const isToday = date === today;

  const handlePreviousDay = () => {
    const prevDay = subDays(parseISO(date), 1);
    setDate(format(prevDay, "yyyy-MM-dd"));
  };

  const handleNextDay = () => {
    const nextDay = addDays(parseISO(date), 1);
    setDate(format(nextDay, "yyyy-MM-dd"));
  };

  const hasTimesheets = timesheetData.some(
    (data: any) => data.timesheets.length > 0
  );

  const allApproved = timesheetData.every((data: any) =>
    data.timesheets.every((timesheet: any) => timesheet.approved)
  );

  return (
    <div className="mb-6">
      <div className="flex flex-row justify-between items-center">
        <h1 className="text-2xl font-bold">Timesheets</h1>
        <Button
          variant="default"
          className="bg-green-700 hover:bg-green-600 text-white"
          onClick={approveAllTimesheets}
          disabled={!hasTimesheets || allApproved}
        >
          Approve Timesheets
        </Button>
      </div>
      <div className="mt-2 flex items-center gap-6">
        <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
          <PopoverTrigger asChild>
            <div className="flex items-center gap-0.5 cursor-pointer text-sm font-medium hover:underline">
              <h3>{formatDateWithOrdinal(parseISO(date))}</h3>
              <FontAwesomeIcon
                icon={faCalendar}
                className="ml-1 h-4 w-4 opacity-50"
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={parseISO(date)}
              onSelect={(newDate) => {
                if (newDate) {
                  setDate(format(newDate, "yyyy-MM-dd"));
                  setDatePopoverOpen(false);
                }
              }}
            />
          </PopoverContent>
        </Popover>

        <div
          className="flex items-center gap-1 cursor-pointer text-sm font-medium"
          onClick={handlePreviousDay}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
          Previous Day
        </div>

        <div
          className={cn(
            "flex items-center gap-1 cursor-pointer text-sm font-medium",
            isToday && "text-gray-400 cursor-default"
          )}
          onClick={isToday ? undefined : handleNextDay}
        >
          Next Day
          <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
};

const parseTime = (dateString: string) => {
  const date = parseISO(dateString);
  let hour = date.getHours();
  const minute = date.getMinutes();
  const meridiem: "AM" | "PM" = hour >= 12 ? "PM" : "AM";

  // Convert to 12-hour format
  if (hour > 12) hour -= 12;
  if (hour === 0) hour = 12;

  return { hour, minute, meridiem };
};

export default Timesheets;
